import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Handle, Position, NodeProps, useUpdateNodeInternals } from 'reactflow';
import { MappingNodeData, HandleConfig } from '../../types';
import { VisibilityOption } from '../../components/shared/LabelWithVisibilityToggle';

// Extended interface to include additional properties used in transformations
interface ExtendedMappingNodeData extends MappingNodeData {
  transformationTypeVisibility?: VisibilityOption;
  category?: string;
  categoryVisibility?: VisibilityOption;
}
import { Wand2 } from 'lucide-react';
import useStore from '../../store/useStore';
import { nodeColors } from '../../components/left-panel/constants';

const calculateHandleStyle = (index: number, total: number, handlePosition: Position): React.CSSProperties => {
  const calcOffset = 5;
  const percentage = (index + 1) * (100 / (total + 1));

  switch (handlePosition) {
    case Position.Top:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, top: `-${calcOffset}px` };
    case Position.Bottom:
      return { left: `calc(${percentage}% - ${calcOffset}px)`, bottom: `-${calcOffset}px` };
    case Position.Left:
    case Position.Right:
    default:
      return { top: `calc(${percentage}% - ${calcOffset}px)` };
  }
};

const TransformationsNode = memo(({ data, selected, id }: NodeProps<ExtendedMappingNodeData>) => {
  const { setSelectedNode } = useStore();
  const updateNodeInternals = useUpdateNodeInternals();

  useEffect(() => {
    if (Array.isArray(data.handles)) {
      updateNodeInternals(id);
    }
  }, [id, data.handles, updateNodeInternals]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedNode({ id, type: 'transformations', data, position: { x: 0, y: 0 } });
  }, [id, data, setSelectedNode]);

  return (
    <div
      onClick={handleClick}
      className={`
        inline-block min-w-[128px] max-w-[320px] pl-3 pr-4 py-2 rounded-lg bg-white dark:bg-gray-700 border transition-all cursor-pointer shadow-sm dark:shadow-sm-dark hover:shadow-md dark:hover:shadow-md-dark
          ${selected 
            ? "border-yellow-500 shadow-yellow-100 outline outline-4 outline-yellow-500/20 outline-offset-0" 
            : "border-gray-300 dark:border-gray-600"
          }
      `}
    >
      <div className="flex items-center gap-2 node-content w-full">
        <div className={`p-1.5 rounded ${selected ? "bg-yellow-200" : nodeColors["transformations"].bg}`}>
          <Wand2 className={`w-4 h-4 ${selected ? "text-yellow-600" : nodeColors["transformations"].text}`} />
        </div>
        <div className="min-w-0 content-container w-full">
          {/* Name - only show if visibility is not hidden */}
          {data.nameVisibility !== "hidden" && (
            <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate w-full">
              {data.name || data.label || 'Transformations'}
            </h3>
          )}

          <div className="flex flex-wrap gap-1">
            {/* Combined Category and Type - show if at least category exists and its visibility is not hidden */}
            {data.category && data.categoryVisibility !== "hidden" && (
              <span
                className="mt-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium truncate max-w-full bg-yellow-100 text-yellow-800"
              >
                {data.transformationType && data.transformationType.trim() !== "" && data.transformationTypeVisibility !== "hidden" 
                  ? `${data.category}: ${data.transformationType}`
                  : data.category}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Notes - only show if visibility is not hidden and we have notes */}
      {data.notes && data.notes.trim() !== "" && data.notesVisibility !== "hidden" && (
        <div className="mt-2 text-[8px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 px-2 py-1 rounded max-h-[80px] overflow-y-auto whitespace-pre-line">
          {data.notes}
        </div>
      )}

      {useMemo(() => {
        const handles = Array.isArray(data.handles) ? [...data.handles] : [];

        const handlesByPosition = handles.reduce((acc, handle) => {
          if (!acc[handle.position]) {
            acc[handle.position] = [];
          }
          acc[handle.position].push(handle);
          return acc;
        }, {} as Record<Position, HandleConfig[]>);

        if (handles.length > 0) {
          return Object.entries(handlesByPosition).map(([_, positionHandles]) =>
            positionHandles.map((handle, index) => (
              <Handle
                key={`${handle.id}-${handle.color}`}
                id={handle.id}
                type={handle.type}
                position={handle.position as Position}
                className="!w-3 !h-3 !border-2 !border-white dark:!border-gray-700"
                style={{
                  ...calculateHandleStyle(index, positionHandles.length, handle.position),
                  backgroundColor: handle.color,
                }}
              />
            ))
          );
        } else {
          return null;
        }
      }, [data.handles])}
    </div>
  );
});

TransformationsNode.displayName = 'TransformationsNode';

export default TransformationsNode;
